import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'
import { stories } from '@opallabs/api/v2'
import { queryKeys } from './queryKeys'
import { FilterOption } from './types'

const storyCategoryUuid = uuidv4()

/**
 * How many stories one request carries. The endpoint has honoured this for
 * years, so a workspace of a few thousand stories still costs one request.
 */
const PAGE_LIMIT = 2000

type StoryDocument = Awaited<ReturnType<typeof stories.read>>

/**
 * Every story of the workspace, however many pages it takes.
 *
 * `/stories/v2` counts nothing in its answer, so the end of the list is a page
 * that comes back short. One page alone would answer with whatever it held and
 * say nothing about the rest, which would leave a workspace with more stories
 * than that quietly unable to filter by the ones past the end.
 */
const readAllStories = async (workspaceId: number): Promise<StoryDocument> => {
  const pages: StoryDocument[] = []

  for (let offset = 0; ; offset += PAGE_LIMIT) {
    const page = await stories.read({
      page: { limit: PAGE_LIMIT, offset },
      filter: { brand_id: [workspaceId] },
    })
    pages.push(page)
    if (page.data.length < PAGE_LIMIT) break
  }

  return pages.length === 1
    ? pages[0]
    : { ...pages[0], data: pages.flatMap((page) => page.data) }
}

export const useStoryFilters = ({
  enabled,
  workspaceId,
}: {
  enabled: boolean
  workspaceId: number
}): UseQueryResult<FilterOption | null> =>
  useQuery({
    queryKey: queryKeys.storyFilters(workspaceId),
    queryFn: () => readAllStories(workspaceId),
    enabled,
    select: (data): FilterOption | null => {
      const stories = data.data

      if (stories.length === 0) return null

      return {
        divider: false,
        groupOrAble: false,
        id: `stories-${storyCategoryUuid}`,
        uuid: storyCategoryUuid,
        inclusivity: false,
        title: 'Stories',
        icon: 'gem-story-filled-dark',
        childOptions: stories.map(({ id, attributes }) => {
          return {
            color: attributes.color || 'n1',
            divider: false,
            groupOrAble: false,
            icon: 'gem-story-filled-dark',
            id: `story-${attributes.uuid as string}`,
            uuid: attributes.uuid as string,
            inclusivity: false,
            title: attributes.name,
            payload: {
              type_key: 'stories',
              object: { id: typeof id === 'string' ? parseInt(id, 10) : id },
            },
          }
        }),
      }
    },
  })
